import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import { createCoupon } from "@/api/coupons";
import { useCouponsStore } from "@/stores/Coupons";
import { useBetslipStore } from "@/stores/Betslip";
import { useUserManager } from "@/tanstackQuery/queries";
import { CouponsQueryKeys } from "@/tanstackQuery/queries/coupons/couponsQueryKeys";
import { explainMutationError, notifyMutationError } from "@/utils/mutationError";

// Where a refused placement is shown. "toast" is the app-wide default; the
// Zero betslip passes "inline" and renders the store's `placementError` in
// its own notice instead (the same sentence, next to the slip it concerns).
export type PlacementErrorSurface = "toast" | "inline";

export const usePlaceBetslipCoupon = (
  options: { errorSurface?: PlacementErrorSurface } = {}
) => {
  const errorSurface = options.errorSurface ?? "toast";
  const t = useTranslations();
  const setIsLocked = useBetslipStore((s) => s.setIsLocked);
  const setPlacementError = useBetslipStore((s) => s.setPlacementError);
  const setSavedCoupon = useCouponsStore((s) => s.setSavedCoupon);
  const setIsSavingInProgress = useCouponsStore((s) => s.setIsSavingInProgress);
  const clearBetslip = useBetslipStore((s) => s.clear);
  const userManager = useUserManager();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCoupon,
    // A mutation-level onError replaces the query client's default (the
    // toast), so the toast is re-issued here for the surfaces that want it.
    onError: (error) => {
      setPlacementError(explainMutationError(t, error));
      if (errorSurface === "toast") notifyMutationError(t, error);
    },
    onSuccess: (data) => {
      setSavedCoupon(data);
      queryClient.invalidateQueries({ queryKey: [CouponsQueryKeys.History] });
      // The paginated coupon list (admin coupon list + the zero theme's
      // "Kuponlarım") was left stale after a bet: its key was missing here, so a
      // freshly placed coupon only appeared after a hard reload.
      queryClient.invalidateQueries({
        queryKey: [CouponsQueryKeys.HistoryPaginated],
      });

      queryClient.invalidateQueries({
        queryKey: [CouponsQueryKeys.UserHistory],
      });
      queryClient.invalidateQueries({
        queryKey: [CouponsQueryKeys.UserHistoryInfinite],
      });
      queryClient.invalidateQueries({ queryKey: [CouponsQueryKeys.Statement] });
      queryClient.invalidateQueries({
        queryKey: [CouponsQueryKeys.LastCoupon],
      });

      queryClient.invalidateQueries({ queryKey: [CouponsQueryKeys.Names] });
    },
    onMutate: () => {
      setPlacementError(null);
      setIsSavingInProgress(true);
      setIsLocked(true);
    },
    onSettled: () => {
      setIsSavingInProgress(false);
      setIsLocked(false);
    },
  });
};
